import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NgFor, NgIf } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { EnterpriseAccessAreaDto } from '../../interfaces/enterprise-access-area-dto';
import { EnterpriseAccessAreaService } from '../../services/enterprise-access-area.service';
import { ConfirmationDialogService } from '../../services/confirmation-dialog.service';

/**
 * The "Enhance Authorization" card: one switch per RBAC product, on or off for the whole enterprise (IAM-2744). Products, names and
 * order all come from the API; the card is hidden when none is configured.
 */
@Component({
    selector: 'mmoga-enterprise-access-areas',
    standalone: true,
    imports: [NgIf, NgFor],
    templateUrl: './enterprise-access-areas.component.html',
    styleUrl: './enterprise-access-areas.component.scss'
})
export class EnterpriseAccessAreasComponent implements OnChanges {
    private readonly enterpriseAccessAreaService = inject(EnterpriseAccessAreaService);
    private readonly confirmationDialogService = inject(ConfirmationDialogService);
    private readonly toastrService = inject(ToastrService);

    private static readonly TOAST_TITLE = 'Enhance Authorization';

    @Input() enterpriseId = 0;

    areas: EnterpriseAccessAreaDto[] = [];
    isLoading = false;
    hasLoadError = false;
    /** Names of the products whose PUT is in flight. */
    savingNames = new Set<string>();

    /** A new enterprise drops the previous one's rows and in-flight state at once, so nothing stale stays clickable. */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['enterpriseId']) {
            this.areas = [];
            this.hasLoadError = false;
            this.savingNames = new Set<string>();
            if (this.enterpriseId) {
                this.loadAreas();
            }
        }
    }

    /** Answers that arrive after the enterprise changed are ignored. */
    loadAreas(): void {
        const enterpriseId = this.enterpriseId;
        this.isLoading = true;
        this.hasLoadError = false;
        this.enterpriseAccessAreaService.getAccessAreas(enterpriseId).subscribe({
            next: areas => {
                if (this.isCurrent(enterpriseId)) {
                    this.areas = areas;
                    this.isLoading = false;
                }
            },
            error: () => {
                if (this.isCurrent(enterpriseId)) {
                    this.areas = [];
                    this.hasLoadError = true;
                    this.isLoading = false;
                }
            }
        });
    }

    /**
     * The switch shows the server's value until the change is confirmed and saved, so the click itself never flips it. The change is
     * pinned to the enterprise the click was made on, and dropped if the enterprise changed while the confirmation was open.
     */
    onSwitchClick(event: Event, area: EnterpriseAccessAreaDto): void {
        event.preventDefault();
        if (this.savingNames.has(area.name)) {
            return;
        }
        const enterpriseId = this.enterpriseId;
        const enable = !area.enabled;
        this.confirmationDialogService.confirm(
            `Turn ${enable ? 'on' : 'off'} ${area.displayName}`,
            enable ? this.turnOnMessage(area.displayName) : this.turnOffMessage(area.displayName),
            `Confirm & Turn ${enable ? 'On' : 'Off'}`,
            'Cancel',
            'md'
        ).then(confirmed => {
            if (confirmed && this.isCurrent(enterpriseId)) {
                this.save(enterpriseId, area, enable);
            }
        }).catch(() => { /* dismissed */ });
    }

    /** Saves for the pinned enterprise; the answer only updates the card while that enterprise is still shown. */
    private save(enterpriseId: number, area: EnterpriseAccessAreaDto, enable: boolean): void {
        this.savingNames.add(area.name);
        this.enterpriseAccessAreaService.setEnabled(enterpriseId, area.name, enable).subscribe({
            next: updated => {
                if (this.isCurrent(enterpriseId)) {
                    this.savingNames.delete(area.name);
                    this.areas = this.areas.map(each => each.name === updated.name ? updated : each);
                    this.toastrService.success(`${updated.displayName} turned ${updated.enabled ? 'on' : 'off'}.`, EnterpriseAccessAreasComponent.TOAST_TITLE);
                }
            },
            error: (error: HttpErrorResponse) => {
                if (this.isCurrent(enterpriseId)) {
                    this.savingNames.delete(area.name);
                    this.onSaveError(error, area, enable);
                }
            }
        });
    }

    private isCurrent(enterpriseId: number): boolean {
        return enterpriseId === this.enterpriseId;
    }

    private onSaveError(error: HttpErrorResponse, area: EnterpriseAccessAreaDto, enable: boolean): void {
        if (error.status === 409 || error.status === 404) {
            const message = error.status === 409
                ? `${area.displayName} was changed at the same time by another admin. The list was reloaded; check it and try again if needed.`
                : `${area.displayName} is no longer available. The list was reloaded.`;
            this.toastrService.error(message, EnterpriseAccessAreasComponent.TOAST_TITLE);
            this.loadAreas();
            return;
        }
        this.toastrService.error(`Couldn't turn ${enable ? 'on' : 'off'} ${area.displayName}. Nothing was changed; try again.`,
                                 EnterpriseAccessAreasComponent.TOAST_TITLE);
    }

    private turnOffMessage(name: string): string {
        return `Everyone in this enterprise's groups loses the ${name} permissions granted through groups, right away. `
            + `Groups, members and custom ${name} roles are kept, and come back if it's turned on again. `
            + 'Other products in the same groups keep working.';
    }

    private turnOnMessage(name: string): string {
        return `Enterprise admins can create ${name} roles and add them to groups. If ${name} was used before, the groups, members `
            + `and custom roles kept from then grant their ${name} permissions again right away, including members added while it was off.`;
    }
}
